/**
 * StudioShell — JEDEN uniwersalny shell APISAI dla wszystkich trybów.
 *
 *  Tryby (mode):
 *    'workspace'  — light · dashboard / wizard / CRM / messages / settings
 *    'studio'     — dark · Hero Creator / Post Creator / branding
 *
 *  Cel: user czuje, że jest w JEDNYM produkcie z różnymi trybami pracy
 *  (jak Canva: jasny shell + dark creative canvas).
 *
 *  Props:
 *    mode         — 'workspace' | 'studio'  (domyślnie 'workspace')
 *    title        — tytuł kontekstu (np. "Tworzenie profilu hodowli")
 *    contextLabel — breadcrumb po APISAI (np. "Profil hodowli")
 *    topbarSlot   — opcjonalny custom topbar content (progress + Save/Next dla wizarda)
 *    children     — main content
 *    activeKey    — override active sidebar item (auto-detect z URL jeśli pominięte)
 *    sidebarMuted — true = dimmed sidebar (np. wizard "construction mode")
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Menu, X, ChevronRight, Lock, ArrowLeft, ArrowRight, Check, ShieldCheck, Sparkles,
  Home as HomeIcon, ShoppingBag, LogOut,
  LayoutDashboard, Inbox, Megaphone as MegaphoneIcon, User as UserIcon,
  Dog, Briefcase, Repeat,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { buildApisaiSidebarItems, buildAdminSidebarItems, isAdminUser, detectActiveSidebarKey, computeSidebarStates, filterItemsForStartMode } from './sidebarItems';
import { useGuidedProgress } from '@/hooks/useGuidedProgress';
import { useNewLeadsCount } from '@/hooks/useNewLeadsCount';
import ApisAssistant from '@/components/assistant/ApisAssistant';
import InstallPWA from '@/components/pwa/InstallPWA';
import InstallBanner from '@/components/pwa/InstallBanner';
import PushOnboarding from '@/components/pwa/PushOnboarding';
import UnansweredLeadsBanner from '@/components/panel/UnansweredLeadsBanner';

/* ─────────────── SIDEBAR (workspace + studio aware) ─────────────── */
/* Po FINALNYM KIERUNKU: sidebar JEST ZAWSZE NAVY (dark) — w obu trybach.
 * Mode rozróżnia tylko brand label + topbar/main. Dark sidebar daje produktowi
 * kontrast, premium feel i charakter (Linear/Cron/Notion style). */
function StudioSidebar({ mode, items, adminItems = [], activeKey, states, onItemClick, muted, isMobile, onClose, breedAccent = 'var(--apisai-primary)', newLeadsCount = 0 }) {
  const isStudio = mode === 'studio';
  // Style overrides driven by breedContext — fallback do APISAI orange gdy brak rasy.
  // Reference: DNA §9 (Breed-Aware Context jako core engine).
  const accentStyle = {
    '--breed-accent': breedAccent,
    '--breed-accent-soft': `${breedAccent}1A`, // 10% opacity hex
    '--breed-accent-ring': `${breedAccent}40`, // 25% opacity hex
  };
  return (
    <aside
      style={accentStyle}
      className={[
        'flex flex-col w-64 flex-shrink-0 h-full apisai-navy-sidebar',
        muted ? 'opacity-95' : '',
      ].join(' ')}
      data-testid={`studio-sidebar-${mode}`}
      data-breed-accent={breedAccent}
    >
      {/* Brand */}
      <div
        className="h-[60px] flex items-center gap-2.5 px-5 cursor-pointer flex-shrink-0 border-b border-[var(--shell-sidebar-border)]"
        onClick={() => onItemClick('/panel')}
      >
        <img
          src="/logo-apisai.png"
          alt="APISAI"
          className="w-9 h-9 object-contain flex-shrink-0 drop-shadow-[0_8px_24px_rgba(249,115,22,0.45)]"
        />
        <div className="flex-1 min-w-0">
          <div className="font-bold tracking-tight text-base text-white">
            APISAI
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--apisai-primary)]">
            {isStudio ? 'Creative Studio' : 'Workspace'}
          </div>
        </div>
        {isMobile && (
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Obszar przewijany: pozycje menu + sekcja admina.
          flex-1 + min-h-0 + overflow-y-auto → długie menu (Suki / Reproduktory / Mioty)
          jest zawsze w pełni przewijane, a stopka pozostaje przypięta na dole i niczego
          nie zasłania (naprawa uciętych pozycji menu na niższych ekranach). */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
      {/* Items */}
      <nav className="px-2.5 py-3 space-y-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          const st = states?.[it.key] || { state: 'available' };
          const isActive = st.state === 'active';
          const isLocked = st.state === 'locked';
          const isNext = st.state === 'next';
          const isDone = st.state === 'done';
          const isComingSoon = !!it.comingSoon;
          return (
            <div key={it.key}>
            {it.groupLabel && (
              <div className="px-2.5 pt-3 pb-1.5">
                <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-white/35">
                  {it.groupLabel}
                </span>
              </div>
            )}
            <button
              key={it.key}
              onClick={() => {
                if (isComingSoon) {
                  toast('Wkrótce dostępne', { description: 'Pracujemy nad tym modułem — pojawi się już niedługo.' });
                } else if (isLocked) {
                  toast(st.lockReason || 'Wkrótce dostępne', {
                    description: 'Wróć po pierwszej publikacji — wszystko zaczyna się w "Moje profile".',
                    action: {
                      label: 'Moje profile',
                      onClick: () => onItemClick('/panel/moje'),
                    },
                  });
                } else {
                  onItemClick(it.to);
                }
              }}
              data-testid={`studio-sidebar-${it.key}`}
              data-state={isComingSoon ? 'coming-soon' : st.state}
              className={[
                'relative group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all',
                isActive
                  ? 'bg-[var(--shell-sidebar-active-bg)] ring-1 ring-[var(--shell-sidebar-active-ring)] shadow-[0_12px_34px_rgba(249,115,22,0.12)]'
                  : (isLocked || isComingSoon)
                    ? 'opacity-55 hover:opacity-80 cursor-pointer'
                    : 'hover:bg-[var(--shell-sidebar-hover)]',
              ].join(' ')}
            >
              {/* Lewy pasek dla active — breed-aware accent */}
              {isActive && (
                <span
                  className="absolute -left-0.5 top-1/2 -translate-y-1/2 w-1 rounded-full h-8"
                  style={{ background: 'var(--breed-accent)' }}
                />
              )}
              <div
                className={[
                  'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-colors',
                  isActive
                    ? 'text-white'
                    : isDone
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : isNext
                        ? ''
                        : 'text-white/55 group-hover:text-white',
                ].join(' ')}
                style={isActive
                  ? { background: 'var(--breed-accent)' }
                  : isNext
                    ? { background: 'var(--breed-accent-soft)', color: 'var(--breed-accent)' }
                    : undefined}
              >
                {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={[
                    'text-[13px] font-semibold leading-tight truncate flex items-center gap-1.5',
                    isActive ? 'text-white' : 'text-white/90',
                  ].join(' ')}
                >
                  <span className="truncate">{it.label}</span>
                  {it.key === 'messages' && newLeadsCount > 0 && (
                    <span
                      data-testid="sidebar-new-leads-badge"
                      className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none text-white"
                      style={{ background: 'var(--apisai-primary)' }}
                    >
                      {newLeadsCount > 99 ? '99+' : newLeadsCount}
                    </span>
                  )}
                </div>
                <div
                  className={[
                    'text-[10px] truncate mt-0.5 flex items-center gap-1',
                    isNext ? 'font-semibold' : 'text-white/40',
                  ].join(' ')}
                  style={isNext ? { color: 'var(--breed-accent)' } : undefined}
                >
                  {(isLocked || isComingSoon) && <Lock className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />}
                  <span className="truncate">
                    {isNext ? (st.hint || 'Następny krok') : isLocked ? (st.lockReason || 'Wkrótce') : it.sub}
                  </span>
                </div>
              </div>
              {isNext && (
                <ArrowRight
                  className="w-3.5 h-3.5 flex-shrink-0 animate-pulse"
                  style={{ color: 'var(--breed-accent)' }}
                />
              )}
            </button>
            </div>
          );
        })}
      </nav>

      {/* Admin section — tylko dla ADMIN_EMAILS (breed-aware accent) */}
      {adminItems && adminItems.length > 0 && (
        <div className="px-2.5 pt-2 pb-3 border-t border-[var(--shell-sidebar-border)]">
          <div className="px-2.5 py-2 flex items-center gap-1.5">
            <ShieldCheck
              className="w-3 h-3"
              strokeWidth={2.5}
              style={{ color: 'var(--breed-accent)' }}
            />
            <span
              className="text-[9px] uppercase tracking-[0.18em] font-bold"
              style={{ color: 'var(--breed-accent)' }}
            >
              Panel admina
            </span>
          </div>
          <div className="space-y-0.5">
            {adminItems.map((it) => {
              const Icon = it.icon;
              const isActive = activeKey === it.key;
              return (
                <button
                  key={it.key}
                  onClick={() => onItemClick(it.to)}
                  data-testid={`studio-sidebar-${it.key}`}
                  className={[
                    'relative group w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left transition-all',
                    isActive
                      ? 'bg-[var(--shell-sidebar-active-bg)] ring-1 ring-[var(--shell-sidebar-active-ring)]'
                      : 'hover:bg-[var(--shell-sidebar-hover)]',
                  ].join(' ')}
                >
                  {isActive && (
                    <span
                      className="absolute -left-0.5 top-1/2 -translate-y-1/2 w-1 rounded-full h-6"
                      style={{ background: 'var(--breed-accent)' }}
                    />
                  )}
                  <div
                    className={[
                      'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0',
                      isActive
                        ? 'text-white'
                        : 'text-white/55 group-hover:text-white',
                    ].join(' ')}
                    style={isActive ? { background: 'var(--breed-accent)' } : undefined}
                  >
                    <Icon className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={[
                      'text-[12px] font-semibold leading-tight truncate',
                      isActive ? 'text-white' : 'text-white/85',
                    ].join(' ')}>
                      {it.label}
                    </div>
                    <div className="text-[9px] text-white/40 truncate">{it.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      </div>
      {/* /Obszar przewijany */}

      {/* Footer — APIS (asystent) + Usługi Premium */}
      <div className="flex-shrink-0 px-2.5 pb-3 pt-2 border-t border-[var(--shell-sidebar-border)] space-y-1.5">
        <button
          onClick={() => onItemClick('/panel/pomoc')}
          data-testid="studio-sidebar-apis-assistant"
          className="w-full text-left rounded-2xl p-3 transition-all group hover:-translate-y-0.5"
          style={{
            background: 'linear-gradient(135deg, var(--breed-accent-soft, rgba(249,115,22,0.18)) 0%, rgba(255,255,255,0.04) 100%)',
            border: '1px solid var(--breed-accent-ring, rgba(249,115,22,0.30))',
          }}
        >
          <div className="flex items-start gap-2.5">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
              style={{ background: 'var(--breed-accent, var(--apisai-primary))' }}
            >
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[12px] font-bold leading-tight">
                Asystent APIS
              </p>
              <p className="text-white/55 text-[10px] leading-snug mt-0.5">
                Pomoc w promocji
              </p>
            </div>
          </div>
          <div
            className="mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white text-[11px] font-bold tracking-tight transition-transform group-hover:scale-[1.01]"
            style={{ background: 'var(--breed-accent, var(--apisai-primary))' }}
          >
            Porozmawiajmy
            <ArrowRight className="w-3 h-3" strokeWidth={2.5} />
          </div>
        </button>

        {/* Usługi Premium — mała sekcja pod APIS */}
        <button
          onClick={() => onItemClick('/panel/uslugi-premium')}
          data-testid="studio-sidebar-premium"
          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-[var(--shell-sidebar-hover)] transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--breed-accent, var(--apisai-primary))' }} />
          <span className="text-white/70 group-hover:text-white text-[11px] font-semibold">Usługi Premium</span>
        </button>

        {/* PWA — instaluj jak aplikację (Android/desktop natywny prompt, iOS instrukcja) */}
        <InstallPWA />
      </div>
    </aside>
  );
}

/* ─────────────── TOPBAR (workspace + studio aware) ─────────────── */
function StudioTopbar({ mode, contextLabel, topbarSlot, onOpenDrawer, backTo, backLabel }) {
  const navigate = useNavigate();
  const { user, logout, updateUserPreference, token } = useAuth() || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const menuRef = useRef(null);
  const isStudio = mode === 'studio';

  const profileType = user?.profile_type === 'biznes' ? 'biznes' : 'hodowla';
  const switchProfile = async (target) => {
    if (switching || target === profileType) { setMenuOpen(false); return; }
    setSwitching(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/profile/profile-type`, { profile_type: target }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      updateUserPreference?.('profile_type', target);
      try {
        const lsRaw = localStorage.getItem('user');
        if (lsRaw) { const u = JSON.parse(lsRaw); u.profile_type = target; localStorage.setItem('user', JSON.stringify(u)); }
      } catch {}
      setMenuOpen(false);
      toast.success(target === 'biznes' ? 'Tryb: Biznes' : 'Tryb: Hodowca');
      navigate('/panel');
    } catch {
      toast.error('Nie udało się przełączyć trybu');
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const initials = (user?.name || user?.email || 'A')
    .split(/[\s@]/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '').join('') || 'A';

  return (
    <header
      className={[
        'sticky top-0 z-30 h-[60px] flex-shrink-0 flex items-center px-3 sm:px-5 gap-2 sm:gap-4',
        isStudio
          ? 'bg-[var(--studio-bg-canvas)]/85 backdrop-blur-md border-b border-[var(--studio-border)]'
          : 'apisai-topbar-glass',
      ].join(' ')}
      data-testid="studio-topbar"
    >
      {/* Mobile hamburger (lg-hidden) */}
      <button
        onClick={onOpenDrawer}
        data-testid="studio-topbar-hamburger"
        className={[
          'lg:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0',
          isStudio ? 'text-white/70 hover:bg-white/[0.06]' : 'text-gray-600 hover:bg-[var(--apisai-bg-subtle)]',
        ].join(' ')}
        aria-label="Menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Wróć do listy — escape hatch z kreatorów (puste moduły auto-przenoszą do wizarda) */}
      {backTo && (
        <button
          onClick={() => navigate(backTo)}
          data-testid="studio-topbar-back-btn"
          title={backLabel || 'Wróć do listy'}
          className={[
            'flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-lg text-[13px] font-semibold transition-colors flex-shrink-0',
            isStudio
              ? 'text-white/70 hover:text-white hover:bg-white/[0.06]'
              : 'text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-text-primary)] hover:bg-[var(--apisai-bg-subtle)]',
          ].join(' ')}
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0" />
          <span className="hidden sm:inline">{backLabel || 'Wróć do listy'}</span>
        </button>
      )}

      {/* Breadcrumb */}
      <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => navigate('/panel')}
          className={[
            'text-sm font-semibold transition-colors',
            isStudio ? 'text-white/60 hover:text-white' : 'text-[var(--apisai-text-muted)] hover:text-[var(--apisai-text-primary)]',
          ].join(' ')}
          data-testid="studio-topbar-breadcrumb-root"
        >
          APISAI
        </button>
        {contextLabel && (
          <>
            <ChevronRight className={`w-4 h-4 ${isStudio ? 'text-white/30' : 'text-gray-300'}`} />
            <span className={`text-sm font-semibold ${isStudio ? 'text-white' : 'text-[var(--apisai-text-primary)]'}`}>
              {contextLabel}
            </span>
          </>
        )}
      </div>

      {/* Custom slot (np. progress + Save/Next dla wizarda) */}
      <div className="flex-1 min-w-0">
        {topbarSlot}
      </div>

      {/* Przełącznik trybu — Hodowca / Biznes (stały, widoczny dla każdego klienta) */}
      <div
        className={[
          'hidden sm:flex items-center gap-0.5 p-0.5 rounded-full flex-shrink-0',
          isStudio ? 'bg-white/[0.06] ring-1 ring-white/10' : 'bg-[var(--apisai-bg-subtle)] ring-1 ring-[var(--apisai-border)]',
        ].join(' ')}
        data-testid="topbar-profile-switcher"
      >
        <button
          onClick={() => switchProfile('hodowla')}
          disabled={switching}
          data-testid="topbar-switch-hodowla"
          title="Tryb hodowcy"
          className={[
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold transition-colors disabled:opacity-60',
            profileType === 'hodowla'
              ? 'bg-[var(--apisai-primary)] text-white shadow-sm'
              : (isStudio ? 'text-white/60 hover:text-white' : 'text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-text-primary)]'),
          ].join(' ')}
        >
          <Dog className="w-3.5 h-3.5" /> <span className="hidden md:inline">Hodowca</span>
        </button>
        <button
          onClick={() => switchProfile('biznes')}
          disabled={switching}
          data-testid="topbar-switch-biznes"
          title="Tryb biznes"
          className={[
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold transition-colors disabled:opacity-60',
            profileType === 'biznes'
              ? 'bg-[var(--apisai-primary)] text-white shadow-sm'
              : (isStudio ? 'text-white/60 hover:text-white' : 'text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-text-primary)]'),
          ].join(' ')}
        >
          <Briefcase className="w-3.5 h-3.5" /> <span className="hidden md:inline">Biznes</span>
        </button>
      </div>

      {/* Ghost ecosystem nav — subtelne shortcuts */}
      <nav className="hidden xl:flex items-center flex-shrink-0" data-testid="studio-topbar-ghost-nav">
        <button
          onClick={() => navigate('/panel')}
          className={[
            'px-2 py-1.5 rounded-md text-[12px] font-medium transition-colors',
            isStudio ? 'text-white/50 hover:text-white/90' : 'text-[var(--apisai-text-muted)] hover:text-[var(--apisai-text-primary)]',
          ].join(' ')}
        >
          Panel
        </button>
        <span className={isStudio ? 'w-px h-3 bg-white/12 mx-1' : 'w-px h-3 bg-[var(--polish-border)] mx-1'} aria-hidden="true" />
        <button
          onClick={() => window.open(profileType === 'biznes' ? '/katalog/uslugi' : '/katalog', '_blank')}
          className={[
            'px-2 py-1.5 rounded-md text-[12px] font-medium transition-colors',
            isStudio ? 'text-white/50 hover:text-white/90' : 'text-[var(--apisai-text-muted)] hover:text-[var(--apisai-text-primary)]',
          ].join(' ')}
        >
          Katalog
        </button>
      </nav>

      {/* Avatar menu */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          data-testid="studio-topbar-avatar"
          className={[
            'w-9 h-9 rounded-full font-bold text-xs flex items-center justify-center transition-all ring-1',
            isStudio
              ? 'bg-white/10 text-white ring-white/20 hover:bg-white/15'
              : 'bg-[var(--apisai-primary-soft)] text-[var(--apisai-primary-700)] ring-[var(--apisai-primary-ring)] hover:bg-[var(--apisai-primary-tint)]',
          ].join(' ')}
        >
          {initials}
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-[var(--shadow-popover)] border border-[var(--apisai-border)] py-1.5 z-50"
            data-testid="studio-topbar-avatar-menu"
          >
            <div className="px-3 py-2 border-b border-[var(--apisai-border)]">
              <p className="text-[11px] text-[var(--apisai-text-muted)]">Zalogowany jako</p>
              <p className="text-sm font-semibold text-[var(--apisai-text-primary)] truncate">{user?.email || 'Konto APISAI'}</p>
            </div>
            <div className="px-3 py-2.5 border-b border-[var(--apisai-border)]">
              <p className="text-[11px] text-[var(--apisai-text-muted)] mb-1.5 flex items-center gap-1"><Repeat className="w-3 h-3" /> Tryb pracy</p>
              <div className="flex gap-1.5" data-testid="profile-switcher">
                <button
                  onClick={() => switchProfile('hodowla')}
                  disabled={switching}
                  data-testid="profile-switch-hodowla"
                  className={[
                    'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-60',
                    profileType === 'hodowla'
                      ? 'bg-[var(--apisai-primary-soft)] text-[var(--apisai-primary-700)] ring-1 ring-[var(--apisai-primary-ring)]'
                      : 'text-[var(--apisai-text-secondary)] hover:bg-[var(--apisai-bg-subtle)]',
                  ].join(' ')}
                >
                  <Dog className="w-3.5 h-3.5" /> Hodowca
                </button>
                <button
                  onClick={() => switchProfile('biznes')}
                  disabled={switching}
                  data-testid="profile-switch-biznes"
                  className={[
                    'flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-60',
                    profileType === 'biznes'
                      ? 'bg-[var(--apisai-primary-soft)] text-[var(--apisai-primary-700)] ring-1 ring-[var(--apisai-primary-ring)]'
                      : 'text-[var(--apisai-text-secondary)] hover:bg-[var(--apisai-bg-subtle)]',
                  ].join(' ')}
                >
                  <Briefcase className="w-3.5 h-3.5" /> Biznes
                </button>
              </div>
            </div>
            {[
              { icon: LayoutDashboard, label: 'Panel główny', to: '/panel' },
              { icon: Inbox, label: 'Zapytania', to: '/panel/leady' },
              { icon: UserIcon, label: 'Moje konto', to: '/panel/ustawienia' },
            ].map((item) => (
              <button
                key={item.to}
                onClick={() => { setMenuOpen(false); navigate(item.to); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--apisai-text-primary)] hover:bg-[var(--apisai-primary-soft)] hover:text-[var(--apisai-primary-700)] transition-colors text-left"
              >
                <item.icon className="w-4 h-4 text-[var(--apisai-text-muted)]" />
                {item.label}
              </button>
            ))}
            <div className="border-t border-[var(--apisai-border)] mt-1 pt-1">
              <button
                onClick={() => { setMenuOpen(false); logout?.(); navigate('/'); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--apisai-text-secondary)] hover:bg-[var(--apisai-danger-soft)] hover:text-[var(--apisai-danger)] transition-colors text-left"
              >
                <LogOut className="w-4 h-4" /> Wyloguj
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

/* ─────────────── SHELL ─────────────── */
export default function StudioShell({
  mode = 'workspace',
  contextLabel,
  topbarSlot,
  children,
  activeKey,
  sidebarMuted = false,
  backTo,
  backLabel,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isStudio = mode === 'studio';

  // Fallback: jeśli context user nie ma profile_type, czytaj z localStorage
  // (auto-sync z fetchUser /auth/me przez AuthProvider)
  let lsProfileType = null;
  try {
    const lsRaw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    if (lsRaw) lsProfileType = JSON.parse(lsRaw)?.profile_type;
  } catch {}
  const profileType = user?.profile_type || lsProfileType || 'hodowla';
  const allItems = buildApisaiSidebarItems(profileType);
  const computedActive = activeKey || detectActiveSidebarKey(location.pathname);
  const progress = useGuidedProgress();
  // START MODE: gdy user nie opublikował jeszcze profilu, ukrywamy zaawansowane itemy
  // (Katalog, Promocja, Zapytania, Opinie, Wyniki) zamiast je tylko blokować.
  const { items } = filterItemsForStartMode(allItems, progress);
  const states = computeSidebarStates(items, progress, computedActive);

  // UI FREEZE v1.0 — panel ZAWSZE używa stałego pomarańczowego akcentu APISAI.
  // (Breed-aware paleta dotyczy wyłącznie publicznych stron hodowli, nie wnętrza panelu.)
  const breedAccent = '#F97316';

  // ETAP 5.5 — licznik NOWYCH zapytań (status 'new') do badge przy "Zapytania".
  const newLeadsCount = useNewLeadsCount();

  // Admin section — tylko dla emaili z ADMIN_EMAILS
  const showAdmin = isAdminUser(user);
  const adminItemsList = showAdmin ? buildAdminSidebarItems() : [];

  const goTo = (path) => { setDrawerOpen(false); navigate(path); };

  return (
    <div
      className={[
        'min-h-screen flex',
        isStudio
          ? 'bg-[var(--studio-bg-canvas)] text-[var(--studio-text-primary)]'
          : 'apisai-workspace-bg text-[var(--apisai-text-primary)]',
      ].join(' ')}
      data-testid={`studio-shell-${mode}`}
    >
      {/* DESKTOP SIDEBAR — persistent lg+ */}
      <div className="hidden lg:flex flex-shrink-0 h-screen sticky top-0">
        <StudioSidebar
          mode={mode}
          items={items}
          adminItems={adminItemsList}
          activeKey={computedActive}
          states={states}
          onItemClick={goTo}
          muted={sidebarMuted}
          isMobile={false}
          breedAccent={breedAccent}
          newLeadsCount={newLeadsCount}
        />
      </div>

      {/* MOBILE DRAWER */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="studio-mobile-drawer">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="relative h-full w-72 max-w-[85vw] shadow-2xl animate-in slide-in-from-left duration-200">
            <StudioSidebar
              mode={mode}
              items={items}
              adminItems={adminItemsList}
              activeKey={computedActive}
              states={states}
              onItemClick={goTo}
              muted={false}
              isMobile={true}
              onClose={() => setDrawerOpen(false)}
              breedAccent={breedAccent}
              newLeadsCount={newLeadsCount}
            />
          </div>
        </div>
      )}

      {/* MAIN COLUMN — topbar + content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <StudioTopbar
          mode={mode}
          contextLabel={contextLabel}
          topbarSlot={topbarSlot}
          onOpenDrawer={() => setDrawerOpen(true)}
          backTo={backTo}
          backLabel={backLabel}
        />
        <main className="flex-1 min-w-0">
          {user?.is_demo && (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 bg-amber-500/15 border-b border-amber-500/30 text-amber-800 text-sm" data-testid="demo-mode-banner">
              <span className="font-medium">Oglądasz wersję demo z przykładowymi danymi.</span>
              <Link to="/rejestracja" className="font-bold underline underline-offset-2 hover:text-amber-900">
                Załóż darmowe konto →
              </Link>
            </div>
          )}
          <InstallBanner />
          <PushOnboarding />
          {!isStudio && <UnansweredLeadsBanner count={newLeadsCount} />}
          {children}
        </main>
      </div>

      {/* Proaktywny asystent APIS — pływa na każdym ekranie panelu */}
      <ApisAssistant />
    </div>
  );
}
